import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { createPortal } from "react-dom";
import {
  ArrowLeft, ArchiveRestore, MapPin, User as UserIcon,
  X, KeyRound, Loader2, Home, AlertCircle,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatErr } from "../lib/api";
import { BRAND_COLORS, STATUS_COLORS, withAlpha } from "../lib/bookingStatus";
import { deDateMedium } from "../lib/dateFormat";

/**
 * Feb 2026 · 9 Sep 2026 · User-Directive „Objektverwaltung":
 *
 * Bereich „Entfernte Objekte" — zeigt alle archivierten Objekte
 * (`archived_at != null`) inklusive Owner + Archiv-Metadaten + Grund.
 * Admin kann pro Zeile „Wiederherstellen" auslösen (PIN-gesichert).
 *
 * Vorbedingung fürs Restore: Der ursprüngliche Owner muss aktuell die
 * Rolle 'Kunde' haben. Für Objekte, deren Owner noch auf Gast steht
 * (Downgrade-Archiv), erscheint ein Hinweis „Kunde ist derzeit Gast".
 */

const REASON_LABEL = {
  manual: "Manuell entfernt",
  downgrade_to_guest: "Auto-Archiv beim Downgrade",
};

const REASON_COLOR = {
  manual: STATUS_COLORS.grey,
  downgrade_to_guest: BRAND_COLORS.gold,
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return deDateMedium(iso);
  } catch { return iso; }
}

// Feb 2026 · 9 Sep 2026 · Auto-Purge-Countdown — archivierte Objekte werden
// nach 365 Tagen dauerhaft gelöscht (siehe `execute_scheduled_deletions`).
// `purge_at` kommt vom Backend im /archived-Response.
function daysUntil(iso) {
  if (!iso) return null;
  try {
    const ms = new Date(iso).getTime() - Date.now();
    return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
  } catch { return null; }
}

export default function TeamPropertiesRemoved() {
  const [rows, setRows] = useState(null);
  const [restoreTarget, setRestoreTarget] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/team/properties/archived");
      setRows(data);
    } catch (e) {
      console.error("archived properties load failed:", e);
      toast.error(formatErr(e.response?.data?.detail) || e.message);
      setRows([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Feb 2026 · 9 Sep 2026 · User-Directive „Purge-Preview":
  // Zeige explizit welche Objekte in den nächsten 30 Tagen dauerhaft
  // gelöscht werden — nach purge_at aufsteigend sortiert. Der Admin muss
  // die Liste vor dem Cron-Lauf durchsehen können, damit nichts still
  // verschwindet. Objekte mit purge_at > 30d landen in „Später".
  const { critical, later } = (() => {
    if (!rows) return { critical: [], later: [] };
    const cr = [];
    const lt = [];
    for (const p of rows) {
      const ms = p.purge_at ? new Date(p.purge_at).getTime() - Date.now() : null;
      const days = ms !== null ? Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24))) : null;
      if (days !== null && days <= 30) cr.push({ ...p, _daysLeft: days });
      else lt.push({ ...p, _daysLeft: days });
    }
    cr.sort((a, b) => (a._daysLeft ?? 9999) - (b._daysLeft ?? 9999));
    lt.sort((a, b) => (a._daysLeft ?? 9999) - (b._daysLeft ?? 9999));
    return { critical: cr, later: lt };
  })();

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 md:py-10" data-testid="team-properties-removed-page">
      <div className="mb-3">
        <Link
          to="/team/properties"
          data-testid="team-properties-removed-back"
          className="inline-flex items-center gap-1.5 text-[12px] tracking-widest uppercase text-navy-dark/60 hover:text-navy"
        >
          <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.6} />
          Zurück zur Objekte-Übersicht
        </Link>
      </div>
      <div className="mb-8">
        <div className="label-caps mb-2" style={{ color: BRAND_COLORS.gold }}>Weissblom · Team</div>
        <h1 className="font-display text-4xl md:text-5xl text-navy">Entfernte Objekte</h1>
        <p className="text-[13.5px] text-navy-dark/65 mt-2 max-w-2xl leading-relaxed">
          Archivierte Objekte bleiben mit allen Daten erhalten. Sie können
          jederzeit über den PIN-gesicherten „Wiederherstellen"-Button
          reaktiviert werden — vorausgesetzt der Kunde ist als „Kunde"
          eingetragen. Nach <b>12 Monaten</b> im Archiv werden Objekte
          automatisch dauerhaft aus der Datenbank entfernt (Rechnungen,
          Buchungen und Dokumente bleiben aus steuerrechtlichen Gründen
          erhalten).
        </p>
      </div>

      {/* Feb 2026 · 9 Sep 2026 · Purge-Preview-Banner (30-Tage-Fenster) */}
      {critical.length > 0 && (
        <div
          className="mb-6 border p-4 md:p-5"
          style={{
            borderColor: STATUS_COLORS.red,
            background: withAlpha(STATUS_COLORS.red, 0.06),
          }}
          data-testid="team-properties-purge-preview-banner"
        >
          <div className="flex items-start gap-3">
            <AlertCircle
              className="w-5 h-5 shrink-0 mt-0.5"
              strokeWidth={1.8}
              style={{ color: STATUS_COLORS.red }}
            />
            <div>
              <div
                className="text-[11px] tracking-[0.18em] uppercase font-semibold"
                style={{ color: STATUS_COLORS.red }}
                data-testid="team-properties-purge-preview-title"
              >
                Purge-Vorschau · {critical.length}{" "}
                {critical.length === 1 ? "Objekt" : "Objekte"} in den nächsten 30 Tagen
              </div>
              <p className="text-[12.5px] text-navy-dark/75 leading-relaxed mt-1">
                Diese Objekte werden beim nächsten Purge-Lauf dauerhaft aus der
                Datenbank entfernt. Zum Erhalten: den Kunden zurück auf „Kunde"
                setzen und dann „Wiederherstellen" klicken.
              </p>
            </div>
          </div>
        </div>
      )}

      {rows === null ? (
        <div className="text-[13px] text-navy-dark/50 italic" data-testid="team-properties-removed-loading">
          Lade entfernte Objekte …
        </div>
      ) : rows.length === 0 ? (
        <div
          className="p-8 text-center border border-dashed border-navy/20 text-[13px] text-navy-dark/60"
          data-testid="team-properties-removed-empty"
        >
          Keine entfernten Objekte vorhanden.
        </div>
      ) : (
        <>
          {critical.length > 0 && (
            <section className="mb-8" data-testid="team-properties-removed-critical-section">
              <h2 className="label-caps mb-3" style={{ color: STATUS_COLORS.red }}>
                In den nächsten 30 Tagen · {critical.length}
              </h2>
              <div className="space-y-3">
                {critical.map((p) => (
                  <RemovedPropertyRow
                    key={p.id}
                    p={p}
                    onRestore={() => setRestoreTarget(p)}
                  />
                ))}
              </div>
            </section>
          )}
          {later.length > 0 && (
            <section data-testid="team-properties-removed-later-section">
              <h2 className="label-caps mb-3 text-navy-dark/70">
                Später · {later.length}
              </h2>
              <div className="space-y-3" data-testid="team-properties-removed-list">
                {later.map((p) => (
                  <RemovedPropertyRow
                    key={p.id}
                    p={p}
                    onRestore={() => setRestoreTarget(p)}
                  />
                ))}
              </div>
            </section>
          )}
        </>
      )}

      <PropertyRestoreDialog
        open={!!restoreTarget}
        property={restoreTarget}
        onClose={() => setRestoreTarget(null)}
        onRestored={async () => {
          setRestoreTarget(null);
          await load();
        }}
      />
    </div>
  );
}

function RemovedPropertyRow({ p, onRestore }) {
  const reasonLabel = REASON_LABEL[p.archive_reason] || "Entfernt";
  const reasonColor = REASON_COLOR[p.archive_reason] || STATUS_COLORS.grey;
  const ownerBlocked = p.owner && p.owner.role !== "customer";
  // Auto-Purge-Countdown: rot markieren, wenn <30 Tage übrig sind.
  const purgeIn = daysUntil(p.purge_at);
  const purgeCritical = purgeIn !== null && purgeIn <= 30;
  return (
    <article
      className="bg-white border border-navy/10 p-4 md:p-5 flex items-start gap-4 flex-wrap"
      data-testid={`removed-property-row-${p.id}`}
    >
      <div className="flex-1 min-w-[220px]">
        <div className="flex items-baseline gap-3 flex-wrap mb-1">
          <h3 className="font-display text-xl text-navy leading-tight" data-testid={`removed-property-name-${p.id}`}>
            {p.name}
          </h3>
          <span
            className="inline-flex items-center gap-1 px-2 py-0.5 text-[10.5px] tracking-[0.14em] uppercase text-ivory"
            style={{ background: reasonColor }}
            data-testid={`removed-property-reason-${p.id}`}
          >
            {reasonLabel}
          </span>
        </div>
        <div className="flex items-start gap-1.5 text-[12.5px] text-navy-dark/70 leading-relaxed mb-2">
          <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0 text-navy-dark/40" strokeWidth={1.6} />
          <div data-testid={`removed-property-address-${p.id}`}>
            {p.address}{p.city ? ` · ${p.city}` : ""}{p.country ? ` · ${p.country}` : ""}
          </div>
        </div>
        <div className="flex items-center gap-1.5 text-[12px] text-navy-dark/70">
          <UserIcon className="w-3.5 h-3.5 shrink-0 text-navy-dark/40" strokeWidth={1.6} />
          {p.owner?.id ? (
            <Link
              to={`/team/customers/${p.owner.id}`}
              className="hover:text-gold transition-colors"
              data-testid={`removed-property-owner-${p.id}`}
            >
              {p.owner.name} <span className="text-navy-dark/50">· {p.owner.email}</span>
              {ownerBlocked && (
                <span
                  className="ml-2 text-[10.5px] tracking-[0.14em] uppercase"
                  style={{ color: STATUS_COLORS.red }}
                  data-testid={`removed-property-owner-blocked-${p.id}`}
                >
                  · Kunde ist derzeit {p.owner.role === "guest" ? "Gast" : p.owner.role}
                </span>
              )}
            </Link>
          ) : (
            <span className="italic text-navy-dark/50">Owner nicht mehr vorhanden</span>
          )}
        </div>
        <div className="text-[11px] text-navy-dark/55 mt-2 tabular-nums">
          Entfernt am <b>{fmtDate(p.archived_at)}</b> · von {p.archived_by_name || "—"}
        </div>
        {p.purge_at && (
          <div
            className="text-[11px] mt-1 tabular-nums"
            style={{ color: purgeCritical ? STATUS_COLORS.red : "rgba(11,42,64,0.55)" }}
            data-testid={`removed-property-purge-${p.id}`}
          >
            {purgeIn === 0
              ? <span><b>Wird beim nächsten Purge-Lauf dauerhaft gelöscht</b></span>
              : <span>Auto-Purge in <b>{purgeIn}</b> Tagen · am {fmtDate(p.purge_at)}</span>}
          </div>
        )}
      </div>

      <button
        type="button"
        onClick={onRestore}
        disabled={!p.owner || ownerBlocked}
        data-testid={`removed-property-restore-${p.id}`}
        className="inline-flex items-center gap-2 text-navy border border-navy/20 hover:border-gold px-4 py-2 text-[11px] tracking-[0.18em] uppercase disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
        title={
          !p.owner
            ? "Owner nicht mehr vorhanden — Wiederherstellung nicht möglich"
            : ownerBlocked
              ? "Kunde muss erst wieder auf Rolle 'Kunde' gesetzt werden"
              : "Objekt wiederherstellen (PIN erforderlich)"
        }
      >
        <ArchiveRestore className="w-3.5 h-3.5" strokeWidth={1.8} />
        Wiederherstellen
      </button>
    </article>
  );
}

function PropertyRestoreDialog({ open, property, onClose, onRestored }) {
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) { setPin(""); setBusy(false); }
  }, [open]);

  if (!open || !property) return null;

  const submit = async () => {
    if (!/^\d{4}$/.test(pin)) {
      toast.error("Bitte 4 Ziffern eingeben.");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/team/properties/${property.id}/restore`, { admin_pin: pin });
      toast.success(`Objekt „${property.name}" wiederhergestellt.`);
      onRestored?.();
    } catch (err) {
      const status = err.response?.status;
      if (status === 403) toast.error("PIN ist falsch. Bitte erneut eingeben.");
      else toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ background: "rgba(13,25,48,0.6)" }}
      onClick={onClose}
      data-testid="property-restore-modal"
    >
      <div
        role="dialog"
        aria-modal="true"
        className="bg-white w-full max-w-md relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 text-navy-dark/50 hover:text-navy"
          aria-label="Schliessen"
          data-testid="property-restore-close"
        >
          <X className="w-4 h-4" strokeWidth={1.6} />
        </button>
        <div className="p-6 md:p-8">
          <div
            className="w-11 h-11 rounded-full flex items-center justify-center mb-4"
            style={{ background: withAlpha(BRAND_COLORS.gold, 0.15), color: BRAND_COLORS.goldDark }}
          >
            <ArchiveRestore className="w-5 h-5" strokeWidth={1.6} />
          </div>
          <h2 className="font-display text-2xl text-navy leading-tight mb-1">
            Objekt wiederherstellen
          </h2>
          <p className="text-[13px] text-navy-dark/70 leading-relaxed mb-5">
            <b>{property.name}</b> wird für <b>{property.owner?.name || "—"}</b>
            {" "}reaktiviert und erscheint wieder in der aktiven Objekt-Liste
            sowie in der Kunden-App.
          </p>

          <div className="mb-4">
            <label className="text-[11px] tracking-[0.18em] uppercase text-navy-dark/70 block mb-1.5">
              Bearbeitungs-PIN
            </label>
            <input
              type="password"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={4}
              autoFocus
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submit(); } }}
              placeholder="4 Ziffern"
              data-testid="property-restore-pin"
              className="w-32 bg-white border border-navy/20 focus:border-gold focus:outline-none px-3 py-2 text-[14px] tabular-nums tracking-widest text-navy"
            />
          </div>

          <div className="flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="text-[11px] tracking-widest uppercase text-navy-dark/70 hover:text-navy px-3 py-2"
              data-testid="property-restore-cancel"
            >
              Abbrechen
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={busy || pin.length !== 4}
              data-testid="property-restore-confirm"
              className="inline-flex items-center gap-2 text-ivory px-5 py-2.5 text-[11px] tracking-[0.18em] uppercase hover:opacity-90 disabled:opacity-60"
              style={{ background: BRAND_COLORS.navy }}
            >
              {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={1.8} />}
              <KeyRound className="w-3.5 h-3.5" strokeWidth={1.8} />
              Wiederherstellen
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
